import { Capacitor, registerPlugin } from '@capacitor/core';
import { geolocationService } from './geolocationService';
import { supabase } from './supabaseClient';

// @capacitor-community/background-geolocation registers itself as "BackgroundGeolocation".
// We type the surface we use to avoid a hard dep on its type declarations
// (which may not be installed in CI / web-only builds).
interface BackgroundLocation {
  latitude: number;
  longitude: number;
  accuracy?: number;
  altitude?: number | null;
  altitudeAccuracy?: number | null;
  bearing?: number | null;
  speed?: number | null;
  time?: number;
}

interface AddWatcherOptions {
  requestPermissions?: boolean;
  stale?: boolean;
  distanceFilter?: number;
  backgroundMessage?: string;
  backgroundTitle?: string;
}

interface BackgroundGeolocationPlugin {
  addWatcher(
    options: AddWatcherOptions,
    callback: (location: BackgroundLocation | null, error?: { code?: string; message?: string } | null) => void
  ): Promise<string>;
  removeWatcher(options: { id: string }): Promise<void>;
  openSettings(): Promise<void>;
}

let pluginRef: BackgroundGeolocationPlugin | null = null;
function plugin(): BackgroundGeolocationPlugin | null {
  if (pluginRef) return pluginRef;
  try {
    pluginRef = registerPlugin<BackgroundGeolocationPlugin>('BackgroundGeolocation');
    return pluginRef;
  } catch (e) {
    console.warn('[bg-geo] plugin unavailable', e);
    return null;
  }
}

const MIN_SAVE_INTERVAL_MS = 5 * 60 * 1000; // throttle DB writes to one per 5 min
const HEARTBEAT_INTERVAL_MS = 4 * 60 * 1000; // refresh heartbeat ~ every 4 min even if stationary

let watcherId: string | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let lastSavedAt = 0;
let lastSavedKey = '';

async function callHeartbeat(): Promise<void> {
  try {
    const { error } = await supabase.rpc('heartbeat_bg_location');
    if (error) console.warn('[bg-geo] heartbeat error', error);
  } catch (e) {
    console.warn('[bg-geo] heartbeat exception', e);
  }
}

async function handleLocation(location: BackgroundLocation | null): Promise<void> {
  if (!location) return;
  const approxKey = `${Math.round(location.latitude * 100) / 100}:${Math.round(location.longitude * 100) / 100}`;
  const now = Date.now();
  const enoughTime = now - lastSavedAt >= MIN_SAVE_INTERVAL_MS;
  const movedEnough = approxKey !== lastSavedKey;

  if (!enoughTime && !movedEnough) {
    // Still alive but not worth a DB write — refresh heartbeat only.
    await callHeartbeat();
    return;
  }

  try {
    await geolocationService.saveMyLocation(location.latitude, location.longitude);
    lastSavedAt = now;
    lastSavedKey = approxKey;
  } catch (e) {
    console.warn('[bg-geo] saveMyLocation failed', e);
  }
  await callHeartbeat();
}

export const backgroundGeolocationService = {
  isRunning(): boolean {
    return watcherId !== null;
  },

  async start(opts: { bgTitle: string; bgMessage: string }): Promise<void> {
    if (watcherId) return;
    if (!Capacitor.isNativePlatform()) {
      // Web fallback: just keep the heartbeat fresh while the tab is open.
      if (!heartbeatTimer) {
        await callHeartbeat();
        heartbeatTimer = setInterval(() => { void callHeartbeat(); }, HEARTBEAT_INTERVAL_MS);
      }
      return;
    }

    const bg = plugin();
    if (!bg) return;

    try {
      watcherId = await bg.addWatcher(
        {
          requestPermissions: true,
          stale: false,
          distanceFilter: 200,
          backgroundTitle: opts.bgTitle,
          backgroundMessage: opts.bgMessage,
        },
        (location, error) => {
          if (error) {
            console.warn('[bg-geo] watcher error', error);
            return;
          }
          void handleLocation(location);
        }
      );
      // Initial heartbeat so the cron picks the user up immediately.
      await callHeartbeat();
      if (!heartbeatTimer) {
        heartbeatTimer = setInterval(() => { void callHeartbeat(); }, HEARTBEAT_INTERVAL_MS);
      }
    } catch (e) {
      console.error('[bg-geo] start failed', e);
      watcherId = null;
    }
  },

  async stop(): Promise<void> {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
    if (!watcherId) return;
    const bg = plugin();
    if (!bg) {
      watcherId = null;
      return;
    }
    try {
      await bg.removeWatcher({ id: watcherId });
    } catch (e) {
      console.warn('[bg-geo] removeWatcher failed', e);
    } finally {
      watcherId = null;
    }
  },
};
